import { scopedApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import { DBImportType, DBImportSource, DBImportFieldMapping, DBRecurringImport, DBRecurringFormat } from '../../types/import';
import { EndpointResponse } from '../../types/endpoints';
import { utils } from '../../utils';
import type { WriteRequestOptions } from '../requestOptions';

const validate = (recurringImportId: number, updates: Omit<RequestParams, 'recurringImportId'>) => {
  if (!recurringImportId) {
    throw new Error('Recurring import ID is required');
  }
  if (Object.keys(updates).length === 0) {
    throw new Error('At least one field must be provided for update');
  }
};

type RequestParams = {
  recurringImportId: number;
  name?: string;
  importType?: DBImportType;
  format?: DBRecurringFormat;
  source?: DBImportSource;
  fieldMappings?: DBImportFieldMapping[];
};

type Response = DBRecurringImport;

export const updateRecurringImport = async (
  scope: RequestScope,
  { recurringImportId, name, importType, format, source, fieldMappings }: RequestParams,
  requestOptions: WriteRequestOptions
): Promise<EndpointResponse<Response>> => {
  try {
    const updates: Omit<RequestParams, 'recurringImportId'> = {};
    if (name !== undefined) updates.name = name;
    if (importType !== undefined) updates.importType = importType;
    if (format !== undefined) updates.format = format;
    if (source !== undefined) updates.source = source;
    if (fieldMappings !== undefined) updates.fieldMappings = fieldMappings;

    validate(recurringImportId, updates);

    const response = await scopedApi(scope).patch<EndpointResponse<Response>>(
      `/recurring_import/${recurringImportId}`,
      {
        ...(name !== undefined && { name }),
        ...(importType !== undefined && { import_type: importType }),
        ...(format !== undefined && { format }),
        ...(source !== undefined && {
          source: {
            type: source.type,
            host: source.host,
            port: source.port,
            username: source.username,
            password: source.password,
            frequency: source.frequency,
            glob: source.glob,
          },
        }),
        ...(fieldMappings !== undefined && {
          field_mapping: fieldMappings.map((mapping) => ({
            type: mapping.type,
            identifier_match: mapping.identifier_match,
            rule: mapping.rule,
            source_field: mapping.source_field,
            value: mapping.value,
            acenda_field: mapping.acenda_field,
          })),
        }),
      },
      requestOptions
    );

    return response.data;
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
